import type { FunctionComponent } from 'react';
import React from 'react';
import type { ButtonProps } from '@material-ui/core/Button';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';
import { Checkbox, Radio } from '@material-ui/core';

export interface StepButtonProps extends ButtonProps {
  selected?: boolean;
  changeColorOnSelect?: boolean;
  noLeftPadding?: boolean;
  columnLayout?: boolean;
  useImageLayout?: boolean;
  /**
   * Acts as an option of a list and shows a checkbox.
   */
  checkbox?: boolean;
  radio?: boolean;
  labelLeft?: boolean;
}

/**
 * A button specifically styled for usage in 'Steps'.
 */
const UnstyledStepButton: FunctionComponent<StepButtonProps> = (props) => {
  const {
    children,
    selected,
    className,
    disableRipple = true,
    fullWidth = true,
    size = 'large',
    color = 'primary',
    variant = 'outlined',
    checkbox,
    radio,
    columnLayout,
    startIcon,
    useImageLayout,
    noLeftPadding,
    ...otherProps
  } = props;

  const getEndIcon = (props: StepButtonProps) => {
    if (props.checkbox) {
      return <Checkbox checked={selected} color="primary" style={{ pointerEvents: 'none' }} />;
    }
    if (props.radio) {
      return <Radio checked={selected} color="primary" style={{ pointerEvents: 'none' }} />;
    }
    return undefined;
  };

  const btnProps = {
    className,
    disableRipple,
    fullWidth,
    size,
    color,
    variant,
    endIcon: getEndIcon(props),
    startIcon,
    ...otherProps,
  };

  return <Button {...btnProps}>{children}</Button>;
};

export const StepButton = styled(UnstyledStepButton)`
  // https://stackoverflow.com/questions/65758258/how-to-make-a-div-rounded-only-from-right-and-left
  border-radius: ${({ noLeftPadding }) => (noLeftPadding ? '8px' : '9999px')};
  border: 1px solid
    ${({ theme, selected }) => (selected ? theme.palette.primary.main : `${theme.palette.text.primary}1a`)};

  padding: ${({ noLeftPadding }) => (noLeftPadding && '0 0.5rem') || '0.9rem 2rem'};
  @media (max-width: ${({ theme }) => theme.breakpoints.ipad}px) {
    padding: ${({ noLeftPadding }) => (noLeftPadding && '0 1rem') || '1rem 2rem'};
  }

  ${({ checkbox, noLeftPadding }) =>
    checkbox &&
    `
  justify-content: flex-start;
  ${!noLeftPadding && `padding-left: 2.5rem;`}
  `}

  font-weight: 500;

  :hover {
    background-color: inherit;
    border: 1px solid
      ${({ theme, selected }) => (selected ? theme.palette.primary.main : `${theme.palette.text.primary}1a`)};
  }

  & .MuiButton-label {
    color: ${({ theme, disabled, changeColorOnSelect, selected }) =>
      (changeColorOnSelect && selected && theme.palette.primary.main) || (!disabled && theme.palette.text.primary)};
    font-size: 0.875rem;
    line-height: 1rem;
    text-transform: none;

    ${({ columnLayout }) =>
      columnLayout &&
      `flex-direction: column;
    padding: 1rem 0;`}

    ${({ labelLeft }) =>
      labelLeft &&
      `
        justify-content: flex-start;
        padding-left: 1.5rem;
      `}
  }

  & .MuiButton-startIcon {
    ${({ useImageLayout }) =>
      useImageLayout &&
      `
    position: relative !important;
    left: 0 !important;
    `}

    img {
      width: 100px;
      height: 100px;
      @media (max-width: ${({ theme }) => theme.breakpoints.ipad}px) {
        width: 50px;
        height: 50px;
      }
    }
  }

  & .MuiButton-endIcon {
    ${({ columnLayout }) => columnLayout && `position: relative !important;`}
  }

  & .option-title {
    color: ${({ theme, disabled, changeColorOnSelect, selected }) =>
      (changeColorOnSelect && selected && theme.palette.primary.main) || (!disabled && theme.palette.text.primary)};
    font-size: 0.875rem;
    line-height: 1rem;
    text-transform: none;
    font-weight: 450;

    ${({ useImageLayout }) =>
      useImageLayout &&
      `
    font-size: 1rem;
    padding: 1rem 0;
    `}

    @media (max-width: ${({ theme }) => theme.breakpoints.ipad}px) {
      padding-right: 1rem;
    }
  }

  & .option-description {
    font-size: 10px;
    font-weight: bold;
    color: #ff3975;
    text-align: left;

    ${({ useImageLayout }) =>
      useImageLayout &&
      `
    margin: -1rem 0 0.5rem;
    `}
  }

  & .MuiButton-startIcon {
    position: absolute;
    left: 5%;
  }

  & .MuiButton-endIcon {
    position: absolute;
    right: 5%;
  }

  & .MuiCheckbox-root {
    padding: 0;

    &:not(.Mui-checked) {
      color: ${({ theme }) => `${theme.palette.text.primary}1a`};
    }

    stroke: ${({ theme, selected }) => (selected ? theme.palette.primary.main : '')};

    // Ignore pointer events like hover and click. Let the button handle the events.
    pointerevents: none;
  }

  & .MuiRadio-root {
    padding: 0;

    &:not(.Mui-checked) {
      color: ${({ theme }) => `${theme.palette.text.primary}1a`};
    }

    stroke: ${({ theme, selected }) => (selected ? theme.palette.primary.main : '')};

    // Ignore pointer events like hover and click. Let the button handle the events.
    pointerevents: none;
  }
`;
